/**
 * 2.1 §3 «Conectar mi tienda» (fase 1): API de vendedores (REST /v1 con clave) y feed CSV/XML
 * periódico, detrás del interruptor `seller_api`. Aquí lo puro y compartido: quién puede usarlo,
 * límites por plan, el cuerpo de la API y las entradas/salidas de las callables de la web y el admin.
 *
 * Colecciones (solo Functions escribe):
 *   sellerApiKeys/{sha256(clave)}   — dueño, nombre, prefijo visible, último uso, revocada
 *   sellerApiAccess/{uid}           — bloqueo del admin (`blocked`)
 *   sellerApiItems/{uid}_{hash}     — external_id del vendedor → products/{id} (idempotencia)
 *   sellerFeeds/{id}                — URL del feed, frecuencia, mapa de categorías, último informe
 *   sellerFeedRuns/{id}             — cada importación (programada o «importar ahora»)
 */
import { z } from 'zod';
import { CurrencySchema, LanguageCodeSchema } from '../firestore/primitives.js';
import { PRODUCT_CONDITIONS } from '../firestore/products.js';
import { PRO_TIERS, type ProTier } from '../firestore/users.js';

/** Prefijo de las claves (`nvk_` + 40 caracteres base64url). */
export const SELLER_API_KEY_PREFIX = 'nvk_';
/** Lo que se enseña de una clave ya creada (prefijo + 8 caracteres). */
export const SELLER_API_KEY_DISPLAY_LEN = 12;

export const SELLER_API_LIMITS = {
  max_keys: 5,
  max_feeds: 3,
  requests_per_minute: 120,
  /** Anuncios gestionados por API/feed (con external_id) por plan PRO. */
  items_by_tier: { basic: 500, plus: 2000, business: 10000, elite: 10000 } as Record<
    ProTier,
    number
  >,
  feed_max_bytes: 20 * 1024 * 1024,
  feed_timeout_ms: 60_000,
  /** Errores que se guardan por importación (el resto solo se cuenta). */
  run_max_errors: 50,
  list_page_size: 100,
} as const;

export function itemsLimitFor(tier: string | null | undefined): number {
  return (PRO_TIERS as readonly string[]).includes(tier ?? '')
    ? SELLER_API_LIMITS.items_by_tier[tier as ProTier]
    : SELLER_API_LIMITS.items_by_tier.basic;
}

/**
 * Quién puede conectar su tienda: vendedor PRO (cualquier plan) con el perfil de profesional
 * verificado (DSA art. 30: quien sube catálogo por API/feed es un comerciante), sin veto de cuenta
 * ni bloqueo del admin en `sellerApiAccess`.
 */
export type SellerApiDenial = 'banned' | 'blocked' | 'not_pro' | 'not_trader';
export function sellerApiDenial(u: {
  banned?: boolean;
  blocked?: boolean;
  is_pro?: boolean;
  is_trader?: boolean;
}): SellerApiDenial | null {
  if (u.banned === true) return 'banned';
  if (u.blocked === true) return 'blocked';
  if (u.is_pro !== true) return 'not_pro';
  if (u.is_trader !== true) return 'not_trader';
  return null;
}

// ---------- cuerpo de la API REST ----------

export const SELLER_API_EXTERNAL_ID_RE = /^[A-Za-z0-9._:/-]{1,120}$/;
export const ExternalIdSchema = z.string().trim().regex(SELLER_API_EXTERNAL_ID_RE, 'external_id');

const HttpsUrl = z
  .string()
  .trim()
  .url()
  .max(2000)
  .refine((u) => u.startsWith('https://'), 'https');

/** PUT /v1/listings/{external_id}: crea o reemplaza (idempotente por external_id). */
export const SellerApiListingSchema = z.object({
  title: z.string().trim().min(3).max(120),
  description: z.string().trim().max(4000).default(''),
  price: z.number().min(0).max(1_000_000),
  /** Por defecto, la moneda de la cuenta. */
  currency: CurrencySchema.optional(),
  /** Id de categoría de nuvvu o un valor de tu catálogo que esté en el mapa de categorías del feed. */
  category: z.string().trim().min(1).max(200),
  condition: z.enum(PRODUCT_CONDITIONS).default('new'),
  photos: z.array(HttpsUrl).min(1).max(50),
  /** 0 = sin existencias: el anuncio se pausa; al volver a > 0 se reactiva. */
  quantity: z.number().int().min(0).max(9999).default(1),
  active: z.boolean().default(true),
  brand: z.string().trim().max(60).default(''),
  size: z.string().trim().max(12).default(''),
  color: z.string().trim().max(40).default(''),
  original_price: z.number().min(0).max(1_000_000).nullable().default(null),
  free_shipping: z.boolean().default(false),
  /** Idioma del texto; por defecto, el de la cuenta. */
  language: LanguageCodeSchema.optional(),
});
export type SellerApiListing = z.infer<typeof SellerApiListingSchema>;
export type SellerApiListingRaw = z.input<typeof SellerApiListingSchema>;

/** PATCH /v1/listings/{external_id}: solo lo que cambia (precio, existencias, textos, fotos…). */
export const SellerApiPatchSchema = z
  .object({
    title: z.string().trim().min(3).max(120),
    description: z.string().trim().max(4000),
    price: z.number().min(0).max(1_000_000),
    currency: CurrencySchema,
    category: z.string().trim().min(1).max(200),
    condition: z.enum(PRODUCT_CONDITIONS),
    photos: z.array(HttpsUrl).min(1).max(50),
    quantity: z.number().int().min(0).max(9999),
    active: z.boolean(),
    brand: z.string().trim().max(60),
    size: z.string().trim().max(12),
    color: z.string().trim().max(40),
    original_price: z.number().min(0).max(1_000_000).nullable(),
    free_shipping: z.boolean(),
  })
  .partial()
  .strict()
  .refine((o) => Object.keys(o).length > 0, 'empty_patch');
export type SellerApiPatch = z.infer<typeof SellerApiPatchSchema>;

/** Lo que devuelve la API por cada anuncio. */
export interface SellerApiListingView {
  external_id: string;
  product_id: string;
  status: string;
  title: string;
  price: number;
  currency: string;
  quantity: number;
  category_id: string;
  photos: string[];
  moderation_reason: string | null;
  source: 'api' | 'feed';
  updated_at: string | null;
}

// ---------- feeds ----------

export const FEED_FORMATS = ['auto', 'csv', 'xml'] as const;
export const FEED_FREQUENCIES = ['daily', '6h'] as const;
export const FEED_ON_MISSING = ['pause', 'keep'] as const;
export const FEED_RUN_STATUSES = ['queued', 'running', 'done', 'partial', 'failed'] as const;
export type FeedFrequency = (typeof FEED_FREQUENCIES)[number];
export type FeedRunStatus = (typeof FEED_RUN_STATUSES)[number];

export const FEED_FREQUENCY_MS: Record<FeedFrequency, number> = {
  daily: 24 * 3_600_000,
  '6h': 6 * 3_600_000,
};

export const FeedRunCountsSchema = z.object({
  rows: z.number().int().min(0),
  created: z.number().int().min(0),
  updated: z.number().int().min(0),
  unchanged: z.number().int().min(0),
  deactivated: z.number().int().min(0),
  failed: z.number().int().min(0),
  /** No procesadas en esta pasada (tiempo agotado o límite del plan); siguen en la próxima. */
  deferred: z.number().int().min(0),
});
export type FeedRunCounts = z.infer<typeof FeedRunCountsSchema>;
export const emptyFeedCounts = (): FeedRunCounts => ({
  rows: 0,
  created: 0,
  updated: 0,
  unchanged: 0,
  deactivated: 0,
  failed: 0,
  deferred: 0,
});

export interface FeedRunError {
  /** Fila (CSV, 1 = primera fila de datos) o posición del elemento (XML). */
  row: number;
  external_id: string | null;
  code: string;
  message: string;
}

export interface FeedRunSummary {
  run_id: string;
  status: FeedRunStatus;
  trigger: 'schedule' | 'manual';
  counts: FeedRunCounts;
  errors: FeedRunError[];
  error: string | null;
  started_at_ms: number | null;
  finished_at_ms: number | null;
}

// ---------- callables (web y admin) ----------

export const SellerApiCreateKeyInputSchema = z.object({ name: z.string().trim().min(1).max(60) });
export interface SellerApiCreateKeyOutput {
  key_id: string;
  /** La clave completa: solo se devuelve aquí, una vez. */
  key: string;
  prefix: string;
}
export const SellerApiKeyIdInputSchema = z.object({
  key_id: z.string().regex(/^[a-f0-9]{64}$/),
});

export const SellerFeedInputSchema = z.object({
  feed_id: z.string().min(1).max(60).optional(),
  url: HttpsUrl,
  format: z.enum(FEED_FORMATS).default('auto'),
  frequency: z.enum(FEED_FREQUENCIES).default('daily'),
  on_missing: z.enum(FEED_ON_MISSING).default('pause'),
  /** Tu categoría → id de categoría de nuvvu. */
  category_map: z.record(z.string().trim().min(1).max(200), z.string().min(1).max(60)).default({}),
  enabled: z.boolean().default(true),
});
export type SellerFeedInput = z.infer<typeof SellerFeedInputSchema>;
export const SellerFeedIdInputSchema = z.object({ feed_id: z.string().min(1).max(60) });

export interface SellerApiKeyView {
  key_id: string;
  name: string;
  prefix: string;
  created_at_ms: number | null;
  last_used_at_ms: number | null;
  revoked: boolean;
}
export interface SellerFeedView {
  feed_id: string;
  url: string;
  format: (typeof FEED_FORMATS)[number];
  frequency: FeedFrequency;
  on_missing: (typeof FEED_ON_MISSING)[number];
  category_map: Record<string, string>;
  enabled: boolean;
  running: boolean;
  next_run_at_ms: number | null;
  last_run: FeedRunSummary | null;
}
export interface SellerApiStatusOutput {
  /** Interruptor `seller_api` (o «solo admins» y quien mira es admin). */
  enabled: boolean;
  denial: SellerApiDenial | null;
  tier: string | null;
  items_count: number;
  items_limit: number;
  keys: SellerApiKeyView[];
  feeds: SellerFeedView[];
}

export const SellerApiAdminSetAccessInputSchema = z.object({
  uid: z.string().min(1).max(128),
  blocked: z.boolean(),
  reason: z.string().trim().max(300).default(''),
});
export interface SellerApiAdminSeller {
  uid: string;
  username: string | null;
  is_pro: boolean;
  is_trader: boolean;
  blocked: boolean;
  blocked_reason: string | null;
  active_keys: number;
  last_used_at_ms: number | null;
  feeds: { feed_id: string; url: string; enabled: boolean; last_run: FeedRunSummary | null }[];
  items_count: number;
}
export interface SellerApiAdminOverviewOutput {
  sellers: SellerApiAdminSeller[];
}
